import { DecimalPipe, DatePipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AssetBook, AssetCategory, AssetInsurancePolicy, AssetMaster, CatalogService } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-asset-detail',
  imports: [
    DecimalPipe, DatePipe, AinPageComponent, AinStateComponent, RouterLink, FormsModule,
    ButtonModule, InputTextModule, SelectModule, InputNumberModule, JalaliDateInputComponent
  ],
  template: `
    <ain-page
      [title]="isCreateMode() ? t('catalog.assets.register.createTitle') : t('catalog.assets.register.detailTitle')"
      [description]="t('catalog.assets.register.detailDescription')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!isCreateMode() && !asset()) {
        <ain-state type="error" [title]="t('catalog.unavailable')" [message]="error() || t('catalog.assets.notFound')" />
      } @else {
        <section class="header-card">
          <div>
            <h3>{{ isCreateMode() ? t('catalog.assets.register.newRecord') : asset()!.assetNumber }}</h3>
            <p>{{ isCreateMode() ? t('catalog.assets.register.createHint') : asset()!.status }}</p>
          </div>
          <div class="actions">
            <a [routerLink]="['/assets/register']">{{ t('catalog.action.backToList') }}</a>
            @if (isCreateMode()) {
              <button pButton type="button" [label]="t('catalog.action.save')" [disabled]="saving()" (click)="save()"></button>
            }
          </div>
        </section>

        @if (isCreateMode()) {
          <section class="form-grid">
            <label><span>{{ t('catalog.assets.assetNumber') }}</span><input pInputText [(ngModel)]="form.assetNumber" /></label>
            <label><span>{{ t('catalog.assets.name') }}</span><input pInputText [(ngModel)]="form.name" /></label>
            <label><span>{{ t('catalog.assets.category') }}</span>
              <p-select [options]="categoryOptions()" [(ngModel)]="form.assetCategoryId" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label><span>{{ t('catalog.assets.book') }}</span>
              <p-select [options]="bookOptions()" [(ngModel)]="form.assetBookId" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" appendTo="body" />
            </label>
            <label><span>{{ t('catalog.assets.acquisitionCost') }}</span><input pInputText type="number" [(ngModel)]="form.acquisitionCost" /></label>
            <label><span>{{ t('catalog.assets.costCenter') }}</span><input pInputText [(ngModel)]="form.costCenterCode" /></label>
            <label><span>{{ t('catalog.assets.location') }}</span><input pInputText [(ngModel)]="form.locationCode" /></label>
          </section>
        } @else {
          <section class="summary-grid">
            <article><strong>{{ t('catalog.assets.netBookValue') }}</strong><span>{{ asset()!.netBookValue | number:'1.2-2' }}</span></article>
            <article><strong>{{ t('catalog.assets.accumulatedDepreciation') }}</strong><span>{{ asset()!.accumulatedDepreciation | number:'1.2-2' }}</span></article>
            <article><strong>{{ t('catalog.assets.acquisitionCost') }}</strong><span>{{ asset()!.acquisitionCost | number:'1.2-2' }}</span></article>
            <article><strong>{{ t('catalog.assets.costCenter') }}</strong><span>{{ asset()!.costCenterCode }}</span></article>
            <article><strong>{{ t('catalog.assets.location') }}</strong><span>{{ asset()!.locationCode }}</span></article>
            <article><strong>{{ t('catalog.assets.maintenanceLock') }}</strong><span>{{ asset()!.isMaintenanceLocked ? t('catalog.yes') : t('catalog.no') }}</span></article>
          </section>

          <nav class="tab-nav">
            <button type="button" [class.active]="activeTab() === 'overview'" (click)="activeTab.set('overview')">{{ t('catalog.tab.overview') }}</button>
            <button type="button" [class.active]="activeTab() === 'depreciation'" (click)="activeTab.set('depreciation')">{{ t('catalog.assets.tab.depreciation') }}</button>
            <button type="button" [class.active]="activeTab() === 'insurance'" (click)="activeTab.set('insurance')">{{ t('catalog.assets.tab.insurance') }}</button>
          </nav>

          @if (activeTab() === 'depreciation') {
            <section class="form-grid">
              <label><span>{{ t('catalog.assets.depreciation.runNumber') }}</span><input pInputText [(ngModel)]="depRun.runNumber" /></label>
              <button pButton type="button" [label]="t('catalog.assets.depreciation.run')" [disabled]="runningDep()" (click)="runDepreciation()"></button>
            </section>
          }

          @if (activeTab() === 'insurance') {
            <section class="form-grid">
              <label><span>{{ t('catalog.assets.insurance.insurer') }}</span><input pInputText [(ngModel)]="insuranceForm.insurerName" /></label>
              <label><span>{{ t('catalog.assets.insurance.policyNumber') }}</span><input pInputText [(ngModel)]="insuranceForm.policyNumber" /></label>
              <label><span>{{ t('catalog.assets.insurance.coverage') }}</span><p-inputNumber [(ngModel)]="insuranceForm.coverageAmount" [min]="0" /></label>
              <label><span>{{ t('catalog.assets.insurance.effectiveDate') }}</span><ain-date-input [(ngModel)]="insuranceForm.effectiveDate" /></label>
              <label><span>{{ t('catalog.assets.insurance.expiryDate') }}</span><ain-date-input [(ngModel)]="insuranceForm.expiryDate" /></label>
              <button pButton type="button" [label]="t('catalog.action.save')" [disabled]="savingInsurance()" (click)="saveInsurance()"></button>
            </section>
            @if (insurancePolicies().length) {
              <ul class="insurance-list">
                @for (policy of insurancePolicies(); track policy.id) {
                  <li>
                    <strong>{{ policy.policyNumber }}</strong>
                    <span>{{ policy.insurerName }} · {{ policy.coverageAmount | number:'1.0-0' }}</span>
                    <span>{{ policy.effectiveDate | date:'mediumDate' }} – {{ policy.expiryDate | date:'mediumDate' }}</span>
                  </li>
                }
              </ul>
            } @else {
              <p class="hint">{{ t('catalog.assets.insurance.empty') }}</p>
            }
          }
        }
      }
    </ain-page>
  `,
  styles: [`
    .header-card, .summary-grid, .form-grid { display: grid; gap: 12px; margin-bottom: 16px; }
    .header-card { grid-template-columns: 1fr auto; align-items: start; padding: 16px; border: 1px solid #e5e7eb; border-radius: 12px; }
    .actions { display: flex; gap: 10px; align-items: center; }
    .summary-grid { grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); }
    .summary-grid article { display: grid; gap: 4px; padding: 12px; background: #f8fafc; border-radius: 8px; }
    .form-grid { grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .form-grid label { display: grid; gap: 6px; }
    .tab-nav { display: flex; gap: 8px; margin-bottom: 12px; }
    .tab-nav button { border: 1px solid #d1d5db; background: #fff; padding: 8px 12px; border-radius: 8px; cursor: pointer; }
    .tab-nav button.active { background: #eff6ff; border-color: #3b82f6; color: #1d4ed8; }
    .insurance-list { list-style: none; padding: 0; margin: 0; display: grid; gap: 8px; }
    .insurance-list li { display: grid; gap: 4px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; }
    .hint { color: #64748b; margin: 0; }
  `]
})
export class AssetDetailComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly runningDep = signal(false);
  readonly savingInsurance = signal(false);
  readonly asset = signal<AssetMaster | null>(null);
  readonly insurancePolicies = signal<AssetInsurancePolicy[]>([]);
  readonly error = signal('');
  readonly activeTab = signal('overview');
  readonly categories = signal<AssetCategory[]>([]);
  readonly books = signal<AssetBook[]>([]);

  readonly isCreateMode = computed(() => this.route.snapshot.paramMap.get('id') === 'new');
  readonly categoryOptions = computed(() => this.categories().map(c => ({ label: c.code, value: c.id })));
  readonly bookOptions = computed(() => this.books().map(b => ({ label: b.code, value: b.id })));

  form = {
    assetNumber: '',
    name: '',
    assetCategoryId: '',
    assetBookId: '',
    acquisitionCost: 10000,
    costCenterCode: 'CC-01',
    locationCode: 'LOC-01'
  };

  depRun = { runNumber: '' };

  insuranceForm = {
    insurerName: '',
    policyNumber: '',
    coverageAmount: 10000,
    effectiveDate: '',
    expiryDate: ''
  };

  ngOnInit(): void {
    if (this.isCreateMode()) {
      this.loadLookups(() => this.loading.set(false));
      return;
    }

    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.loading.set(false);
      return;
    }

    this.service.getAsset(id).subscribe({
      next: asset => {
        this.asset.set(asset);
        this.insurancePolicies.set(asset.insurancePolicies ?? []);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('catalog.assets.notFound'));
        this.loading.set(false);
      }
    });
  }

  save(): void {
    if (!this.form.assetNumber.trim() || !this.form.name.trim()) {
      this.error.set(this.t('catalog.assets.requiredFields'));
      return;
    }

    this.saving.set(true);
    const today = new Date().toISOString().slice(0, 10);
    this.service.registerAsset({
      assetNumber: this.form.assetNumber,
      name: this.form.name,
      assetCategoryId: this.form.assetCategoryId,
      assetBookId: this.form.assetBookId,
      offeringId: null,
      serialId: null,
      costCenterCode: this.form.costCenterCode,
      locationCode: this.form.locationCode,
      acquisitionDate: today,
      acquisitionCost: this.form.acquisitionCost,
      depreciationMethod: 'StraightLine',
      usefulLifeMonths: 60,
      salvageValue: 0,
      decliningBalanceRatePercent: 0,
      unitsOfProductionCapacity: 0,
      placeInService: true
    }).subscribe({
      next: created => {
        this.saving.set(false);
        void this.router.navigate(['/assets/register', created.id]);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('catalog.assets.createFailed'));
      }
    });
  }

  saveInsurance(): void {
    const asset = this.asset();
    if (!asset || !this.insuranceForm.insurerName.trim() || !this.insuranceForm.policyNumber.trim()) return;

    this.savingInsurance.set(true);
    this.service.addAssetInsurance(asset.id, {
      insurerName: this.insuranceForm.insurerName,
      policyNumber: this.insuranceForm.policyNumber,
      coverageAmount: this.insuranceForm.coverageAmount,
      effectiveDate: this.insuranceForm.effectiveDate,
      expiryDate: this.insuranceForm.expiryDate
    }).subscribe({
      next: policy => {
        this.insurancePolicies.update(rows => [...rows, policy]);
        this.insuranceForm = { insurerName: '', policyNumber: '', coverageAmount: 10000, effectiveDate: '', expiryDate: '' };
        this.savingInsurance.set(false);
      },
      error: () => this.savingInsurance.set(false)
    });
  }

  runDepreciation(): void {
    const asset = this.asset();
    if (!asset || !this.depRun.runNumber.trim()) return;

    this.runningDep.set(true);
    const today = new Date().toISOString().slice(0, 10);
    this.service.runDepreciation({
      assetBookId: asset.assetBookId,
      runNumber: this.depRun.runNumber,
      periodEndDate: today,
      batchSize: 0,
      enqueueOnly: false
    }).subscribe({
      next: () => {
        this.runningDep.set(false);
        this.ngOnInit();
      },
      error: () => this.runningDep.set(false)
    });
  }

  private loadLookups(done: () => void): void {
    this.service.listAssetCategories().subscribe({
      next: categories => {
        this.categories.set(categories);
        if (categories[0]) this.form.assetCategoryId = categories[0].id;
      }
    });
    this.service.listAssetBooks().subscribe({
      next: books => {
        this.books.set(books);
        if (books[0]) this.form.assetBookId = books[0].id;
        done();
      },
      error: done
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
